import React from "react";
import {TextField as MuiLineQtyField, TextField as MuiMoneyField} from "@material-ui/core";
import Cookies from "js-cookie";

import Framework from "@beardeddevops/react.framework";
import Types from "constants/Types";
import API from "@beardeddevops/react.api";
import {toast} from "react-toastify";
import {
	defaultTransactionRefundMethod,
	isProcessorTender,
	isZelleTender,
	paymentAmountIsActive,
} from "../../utils/paymentMethodLabel";

// Refund preview math. The cash-discount-aware per-line refund formula here mirrors
// `\Abstracts\Utilities::computeRefundLineCashAndTax` on the backend so the preview matches what
// `Database\Transactions_Refunds::Create()` actually persists. The base "checkout totals" order of operations
// for the original sale lives on `Checkout.recalculateTotals` (frontend, JSDoc) and
// `\Abstracts\Utilities::buildSectionFromLines` (backend); the original `tax_json.original` section is the
// source of truth for what was originally taxed.
//
// Penny note: sale-time bucket `tax` can differ by $0.01 from `round(amount * rate / 100, 2)` because the
// snapshot reconciles to `transactions.tax_amount` (last-bucket / header). When a refund line matches an
// original bucket (amount + rate, no restocking fee), we use that bucket's `tax` so the preview matches the receipt.

/** Same formula as `Abstracts\Utilities::effectiveUnitPriceAfterCashDiscount` / `cashDiscount` in constants/Utilities. */
function effectiveUnitPriceAfterCashDiscount(listUnitPrice, cashDiscountPct) {
	const p = Number(cashDiscountPct) || 0;
	if (p <= 0.00001) return listUnitPrice;
	const rate = p / 100;
	return Math.round((listUnitPrice / (1 + rate)) * 100) / 100;
}

/** Transaction row wins (matches PHP `cashDiscountPercentageFromTransaction`); tax_json meta is fallback. */
function parseCashDiscountPctFromTransaction(tx) {
	if (!tx) return 0;
	const fromTx = parseFloat(tx.cash_discount_percentage);
	if (Number.isFinite(fromTx) && fromTx > 0.00001) {
		return fromTx;
	}
	try {
		const raw = tx.tax_json;
		if (raw) {
			const j = typeof raw === "string" ? JSON.parse(raw) : raw;
			if (j && j.meta && j.meta.cash_discount_percentage != null) {
				const p = parseFloat(j.meta.cash_discount_percentage);
				if (Number.isFinite(p) && p > 0) return p;
			}
		}
	} catch (e) {
		// ignore
	}
	return 0;
}

/** `tax_json.current` merchandise totals after transaction discount + trade-ins (matches Utilities::buildSectionFromLines). */
function parseTaxJsonCurrentMerchTotals(taxJsonRaw) {
	try {
		const j = typeof taxJsonRaw === "string" ? JSON.parse(taxJsonRaw) : taxJsonRaw;
		const cur = j?.current;
		if (!cur) {
			return {taxable_post: 0, non_taxable_post: 0};
		}
		return {
			taxable_post: Math.round(parseFloat(cur.taxable_subtotal || 0) * 100) / 100,
			non_taxable_post: Math.round(parseFloat(cur.non_taxable_subtotal || 0) * 100) / 100,
		};
	} catch (e) {
		return {taxable_post: 0, non_taxable_post: 0};
	}
}

function parseCumulativeCashRefunded(taxJsonRaw) {
	try {
		const j = typeof taxJsonRaw === "string" ? JSON.parse(taxJsonRaw) : taxJsonRaw;
		const v = j?.meta?.cumulative_cash_refunded;
		const n = parseFloat(v);
		return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
	} catch (e) {
		return 0;
	}
}

/** Pre–transaction-discount extension for one line's remaining sellable qty (pool for scaling). */
function extensionPreGlobalRemainingOneLine(entry, cashDiscountPct) {
	const pct = Number(cashDiscountPct) || 0;
	if (!entry || entry.transaction_type === "transaction_trade") {
		return null;
	}
	if (entry.transaction_type === "transaction_product") {
		if (entry.refunded === 1) {
			return null;
		}
		const listUnit = parseFloat(entry.price);
		const unit = effectiveUnitPriceAfterCashDiscount(listUnit, pct);
		const disc = parseFloat(entry.discount_amount || 0);
		return {taxable: !!entry.taxable, ext: Math.max(0, Math.round((unit - disc) * 100) / 100)};
	}
	const qRem =
		entry.transaction_type === "transaction_product"
			? 1
			: parseInt(entry.original_quantity, 10);
	if (!Number.isFinite(qRem) || qRem <= 0) {
		return null;
	}
	const fullQ =
		entry.sale_line_quantity != null
			? Math.max(1, parseInt(entry.sale_line_quantity, 10) || 1)
			: Math.max(1, qRem);
	if (entry.transaction_type === "transaction_item" || entry.transaction_type === "transaction_service") {
		const listUnit = parseFloat(entry.price);
		const unit = effectiveUnitPriceAfterCashDiscount(listUnit, pct);
		const discFull = parseFloat(entry.discount_amount || 0);
		const disc = fullQ > 0 ? discFull * (qRem / fullQ) : 0;
		return {taxable: !!entry.taxable, ext: Math.max(0, Math.round((unit * qRem - disc) * 100) / 100)};
	}
	if (entry.transaction_type === "transaction_consignment") {
		const listUnit = parseFloat(entry.price);
		const unit = effectiveUnitPriceAfterCashDiscount(listUnit, pct);
		const discFull = parseFloat(entry.discount_amount || 0);
		const disc = fullQ > 0 ? discFull * (qRem / fullQ) : 0;
		return {taxable: !!entry.taxable, ext: Math.max(0, Math.round((unit * qRem - disc) * 100) / 100)};
	}
	return null;
}

function buildRefundMerchandiseScalingContextFromUi(items, taxJsonRaw, pct) {
	const posts = parseTaxJsonCurrentMerchTotals(taxJsonRaw);
	let tPre = 0;
	let nPre = 0;
	for (const e of items || []) {
		const row = extensionPreGlobalRemainingOneLine(e, pct);
		if (!row) {
			continue;
		}
		if (row.taxable) {
			tPre += row.ext;
		} else {
			nPre += row.ext;
		}
	}
	if (
		posts.taxable_post <= 0.00001 &&
		posts.non_taxable_post <= 0.00001 &&
		(tPre > 0.00001 || nPre > 0.00001)
	) {
		return null;
	}
	return {
		taxable_pre: Math.round(tPre * 10000) / 10000,
		non_taxable_pre: Math.round(nPre * 10000) / 10000,
		taxable_post: posts.taxable_post,
		non_taxable_post: posts.non_taxable_post,
	};
}

function applyMerchandiseScalingToExtension(basePre, taxable, ctx) {
	const B = Math.max(0, Math.round(parseFloat(basePre || 0) * 100) / 100);
	if (!ctx) {
		return B;
	}
	if (taxable) {
		const pre = ctx.taxable_pre || 0;
		const post = ctx.taxable_post || 0;
		if (pre <= 0.00001) {
			return 0;
		}
		return Math.max(0, Math.round(B * (post / pre) * 100) / 100);
	}
	const pre = ctx.non_taxable_pre || 0;
	const post = ctx.non_taxable_post || 0;
	if (pre <= 0.00001) {
		return 0;
	}
	return Math.max(0, Math.round(B * (post / pre) * 100) / 100);
}

/** Mirrors `Abstracts\Utilities::computeRefundLineCashAndTax` */
function parseRestockFeeInput(raw) {
	const s = String(raw ?? "")
		.replace(/,/g, "")
		.trim();
	if (s === "") return 0;
	const n = parseFloat(s);
	if (!Number.isFinite(n) || n < 0) return 0;
	return Math.round(n * 100) / 100;
}

/** Only digits and a single decimal point; strips letters, minus, spaces, second ".", etc. */
function sanitizeRestockFeeInput(raw) {
	if (raw === undefined || raw === null) {
		return "";
	}
	const str = String(raw).replace(/,/g, "");
	let out = "";
	let sawDot = false;
	for (const ch of str) {
		if (ch >= "0" && ch <= "9") {
			out += ch;
			continue;
		}
		if (ch === "." && !sawDot) {
			sawDot = true;
			out += ch;
		}
	}
	return out;
}

/** Shallow copy of `tax_json.original.buckets` for in-place consumption while previewing multiple lines. */
function cloneOriginalBucketPool(taxJsonRaw) {
	try {
		const j = typeof taxJsonRaw === "string" ? JSON.parse(taxJsonRaw) : taxJsonRaw;
		const b = j?.original?.buckets;
		if (!Array.isArray(b) || b.length === 0) {
			return [];
		}
		return b.map((row) => ({...row}));
	} catch (e) {
		return [];
	}
}

/**
 * If this line's taxable extension + rate match a sale bucket and there is no restocking fee, return that
 * bucket's reconciled `tax` and remove the bucket from `pool` so each bucket is used once per preview pass.
 */
function popMatchingOriginalBucketTax(pool, extensionPreTax, rate, feeRaw) {
	if (!pool || pool.length === 0) {
		return null;
	}
	const feeIn = typeof feeRaw === "number" ? feeRaw : parseRestockFeeInput(feeRaw);
	if (feeIn > 0.00001) {
		return null;
	}
	const B = Math.max(0, Math.round(parseFloat(extensionPreTax || 0) * 100) / 100);
	const r = parseFloat(rate) || 0;
	for (let i = 0; i < pool.length; i++) {
		const b = pool[i];
		const amt = Math.round(parseFloat(b.amount ?? 0) * 100) / 100;
		const br = parseFloat(b.rate ?? 0) || 0;
		if (Math.abs(amt - B) > 0.005) {
			continue;
		}
		if (Math.abs(br - r) > 0.00005) {
			continue;
		}
		const tax = Math.round(parseFloat(b.tax ?? 0) * 100) / 100;
		pool.splice(i, 1);
		return tax;
	}
	return null;
}

function computeRefundLineCashAndTax(extensionPreTax, rate, taxable, feeRaw, bucketTaxOverride = null) {
	const B = Math.max(0, Math.round(parseFloat(extensionPreTax || 0) * 100) / 100);
	const feeIn = parseRestockFeeInput(feeRaw);
	const fee = Math.min(feeIn, B);
	const Bnet = Math.max(0, Math.round((B - fee) * 100) / 100);
	if (!taxable) {
		return {cash: Bnet, tax_refunded: 0, fee, subtotal: B, subtotal_after_fee: Bnet};
	}
	const r = parseFloat(rate) || 0;
	let taxRefunded;
	if (bucketTaxOverride != null && fee <= 0.00001) {
		taxRefunded = Math.round(bucketTaxOverride * 100) / 100;
	} else {
		taxRefunded = Math.round(Bnet * (r / 100) * 100) / 100;
	}
	const cash = Math.round((Bnet + taxRefunded) * 100) / 100;
	return {cash, tax_refunded: taxRefunded, fee, subtotal: B, subtotal_after_fee: Bnet};
}

/** Numeric qty for math; 0 if blank/invalid/error so previews do not use bad values. */
function effectiveQtyForSlice(e) {
	if (e.product) return 1;
	if (e.quantity_error) return 0;
	const s = String(e.quantity_display ?? "").trim();
	if (s === "") return 0;
	const n = parseInt(s, 10);
	if (isNaN(n) || n < 1) return 0;
	if (n > (e.original_quantity || 0)) return 0;
	return n;
}

function buildSlicesFromSelected(entries, cashDiscountPct, merchCtx = null) {
	const pct = Number(cashDiscountPct) || 0;
	const slices = [];
	entries.forEach((e) => {
		if (e.transaction_type === "transaction_product") {
			const listUnit = parseFloat(e.price);
			const unit = effectiveUnitPriceAfterCashDiscount(listUnit, pct);
			const disc = parseFloat(e.discount_amount || 0);
			const raw = Math.max(0, Math.round((unit - disc) * 100) / 100);
			slices.push({
				taxable: !!e.taxable,
				extension_pre_tax: applyMerchandiseScalingToExtension(raw, !!e.taxable, merchCtx),
				rate: parseFloat(e.tax_rate || 0),
			});
			return;
		}
		const q = effectiveQtyForSlice(e);
		if (
			(e.transaction_type === "transaction_item" ||
				e.transaction_type === "transaction_service" ||
				e.transaction_type === "transaction_consignment") &&
			q <= 0
		) {
			return;
		}
		const fullQ =
			e.sale_line_quantity != null
				? Math.max(1, parseInt(e.sale_line_quantity, 10) || 1)
				: Math.max(1, q);
		if (e.transaction_type === "transaction_item" || e.transaction_type === "transaction_service") {
			const listUnit = parseFloat(e.price);
			const unit = effectiveUnitPriceAfterCashDiscount(listUnit, pct);
			const discFull = parseFloat(e.discount_amount || 0);
			const disc = fullQ > 0 ? discFull * (q / fullQ) : 0;
			const raw = Math.max(0, Math.round((unit * q - disc) * 100) / 100);
			slices.push({
				taxable: !!e.taxable,
				extension_pre_tax: applyMerchandiseScalingToExtension(raw, !!e.taxable, merchCtx),
				rate: parseFloat(e.tax_rate || 0),
			});
			return;
		}
		if (e.transaction_type === "transaction_consignment") {
			const qty = q;
			const listUnit = parseFloat(e.price);
			const unit = effectiveUnitPriceAfterCashDiscount(listUnit, pct);
			const discFull = parseFloat(e.discount_amount || 0);
			const disc = fullQ > 0 ? discFull * (qty / fullQ) : 0;
			const raw = Math.max(0, Math.round((unit * qty - disc) * 100) / 100);
			slices.push({
				taxable: !!e.taxable,
				extension_pre_tax: applyMerchandiseScalingToExtension(raw, !!e.taxable, merchCtx),
				rate: parseFloat(e.tax_rate || 0),
			});
		}
	});
	return slices;
}

/** Per-line preview: subtotal, fee, tax returned, cash back. */
function lineRefundBreakdown(item, cashDiscountPct, bucketPool, merchCtx = null) {
	const slices = buildSlicesFromSelected([item], cashDiscountPct, merchCtx);
	if (!slices.length) return null;
	const s = slices[0];
	const override =
		bucketPool && bucketPool.length
			? popMatchingOriginalBucketTax(bucketPool, s.extension_pre_tax, s.rate, item.restocking_fee)
			: null;
	const r = computeRefundLineCashAndTax(
		s.extension_pre_tax,
		s.rate,
		s.taxable,
		item.restocking_fee,
		override
	);
	return {
		subtotal: r.subtotal,
		fee: r.fee,
		taxReturned: r.tax_refunded,
		cash: r.cash,
	};
}

export default class TransactionRefund extends Framework.Components.Object {
	constructor(props) {
		super(props, Types.Transactions_Refunds._name);
		this.state.refund_amount = 0;
		this.state.original_transaction_total = 0;
		this.state.original_transaction_method = "cash";
		this.state.has_processor_refund = false;
		this.state.has_zelle_tender = false;
		this.state.method = "cash";
		this.state.previewTaxRefunded = 0;
		this.state.cash_discount_pct = 0;
		this.state.cumulative_cash_refunded = 0;
		this.state.max_refundable_cash_remaining = 0;
	}

	componentDidMount() {
		const first_item = this.props.items[0];
		const original_transaction_total = parseFloat(first_item.transactions.amount);
		const originalTx = first_item.transactions || {};
		const original_transaction_method = originalTx.payment_method || "cash";
		const has_processor_refund = isProcessorTender(originalTx);
		const has_zelle_tender = isZelleTender(originalTx) || paymentAmountIsActive(originalTx.zelle_amount);

		const items = this.props.items
			.filter((item) => !item.refunded || item.refunded === 0)
			.filter((item) => {
				if (item.transaction_type === "transaction_trade") {
					return false;
				}
				if (item.transaction_type === "transaction_product") {
					return item.refunded !== 1;
				}
				const saleQty =
					item.transaction_type === "transaction_product"
						? 1
						: Math.max(1, parseInt(item.quantity, 10) || 1);
				const rq = parseInt(item.refund_quantity, 10);
				const refundedQty = Number.isFinite(rq) ? rq : 0;
				return saleQty > refundedQty;
			})
			.map((item) => {
				const saleLineQty =
					item.transaction_type === "transaction_product"
						? 1
						: Math.max(1, parseInt(item.quantity, 10) || 1);
				const remaining =
					item.transaction_type === "transaction_product"
						? 1
						: saleLineQty - (item.refund_quantity || 0);
				return {
					...item,
					selected: false,
					quantity_error: "",
					restocking_fee: "",
					restocking_fee_error: "",
					sale_line_quantity: saleLineQty,
					original_quantity: remaining,
					quantity_display:
						item.transaction_type === "transaction_product" ? "1" : String(remaining),
					quantity: remaining,
				};
			});

		const cash_discount_pct = parseCashDiscountPctFromTransaction(first_item.transactions);
		const taxJsonRaw = first_item.transactions?.tax_json;
		const cumulative_cash_refunded = parseCumulativeCashRefunded(taxJsonRaw);
		const max_refundable_cash_remaining = Math.max(
			0,
			Math.round((original_transaction_total - cumulative_cash_refunded) * 100) / 100
		);
		const patch = {
			items,
			original_transaction_total,
			original_transaction_method,
			has_processor_refund,
			has_zelle_tender,
			cash_discount_pct,
			cumulative_cash_refunded,
			max_refundable_cash_remaining,
		};
		patch.method = defaultTransactionRefundMethod(originalTx);
		const cookieReg = Cookies.getJSON("register");
		const emptyReg =
			this.state.register === "" ||
			this.state.register === undefined ||
			this.state.register === null;
		if (emptyReg && cookieReg !== undefined && cookieReg !== null && cookieReg !== "") {
			patch.register = cookieReg;
		}

		this.setState(patch, () => this.recalculatePreview());
	}

	recalculatePreview = () => {
		const pct = this.state.cash_discount_pct || 0;
		const taxJsonRaw = (this.state.items || [])[0]?.transactions?.tax_json;
		const merchCtx = buildRefundMerchandiseScalingContextFromUi(this.state.items || [], taxJsonRaw, pct);
		const items = (this.state.items || []).map((row) => {
			if (!row.selected) {
				return {...row, restocking_fee_error: ""};
			}
			const sl = buildSlicesFromSelected([row], pct, merchCtx);
			if (!sl.length) {
				return {...row, restocking_fee_error: ""};
			}
			const B = sl[0].extension_pre_tax;
			const feeNum = parseRestockFeeInput(row.restocking_fee);
			let err = "";
			if (feeNum > B + 0.00001) {
				err = `Cannot exceed line subtotal ($${B.toFixed(2)}).`;
			}
			return {...row, restocking_fee_error: err};
		});
		const selected = items.filter((i) => i.selected);
		const slices = buildSlicesFromSelected(selected, pct, merchCtx);
		const bucketPoolTotals = cloneOriginalBucketPool(taxJsonRaw);
		let totalCash = 0;
		let totalTax = 0;
		selected.forEach((row, i) => {
			const s = slices[i];
			if (!s) return;
			const override = popMatchingOriginalBucketTax(
				bucketPoolTotals,
				s.extension_pre_tax,
				s.rate,
				row.restocking_fee
			);
			const line = computeRefundLineCashAndTax(
				s.extension_pre_tax,
				s.rate,
				s.taxable,
				row.restocking_fee,
				override
			);
			totalCash += line.cash;
			totalTax += line.tax_refunded;
		});
		this.setState({
			items,
			refund_amount: Math.round(totalCash * 100) / 100,
			previewTaxRefunded: Math.round(totalTax * 100) / 100,
		});
	};

	handleSubmit = async (event) => {
		await this.setState({loading: true});
		this.state.transaction = this.props.transaction;

		const selected = (this.state.items || []).filter((i) => i.selected);
		if (selected.length === 0) {
			API.ToastId = toast.error("You must select an item to refund", {
				position: toast.POSITION.TOP_CENTER,
			});
			await this.setState({loading: false});
			return;
		}

		for (const row of selected) {
			if (row.transaction_type === "transaction_product") {
				if (row.refunded === 1) {
					API.ToastId = toast.error("That product line is already refunded.", {
						position: toast.POSITION.TOP_CENTER,
					});
					await this.setState({loading: false});
					return;
				}
				continue;
			}
			const s = String(row.quantity_display ?? "").trim();
			if (s === "") {
				API.ToastId = toast.error("Enter a quantity for each selected line.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
			if (row.quantity_error) {
				API.ToastId = toast.error(row.quantity_error, {position: toast.POSITION.TOP_CENTER});
				await this.setState({loading: false});
				return;
			}
			const n = parseInt(s, 10);
			if (isNaN(n) || n < 1 || n > row.original_quantity) {
				API.ToastId = toast.error("Fix quantity errors before refunding.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
		}

		const pct = this.state.cash_discount_pct || 0;
		const taxJsonSubmit = (selected[0] || {}).transactions?.tax_json;
		const merchCtxSubmit = buildRefundMerchandiseScalingContextFromUi(this.state.items || [], taxJsonSubmit, pct);
		const slices = buildSlicesFromSelected(selected, pct, merchCtxSubmit);
		if (slices.length !== selected.length) {
			API.ToastId = toast.error("Enter a valid quantity for each selected line.", {
				position: toast.POSITION.TOP_CENTER,
			});
			await this.setState({loading: false});
			return;
		}

		for (let i = 0; i < selected.length; i++) {
			if (selected[i].restocking_fee_error) {
				API.ToastId = toast.error("Fix restocking fee errors before refunding.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
			const B = slices[i].extension_pre_tax;
			const f = parseRestockFeeInput(selected[i].restocking_fee);
			if (f > B + 0.00001) {
				API.ToastId = toast.error("Restocking fee cannot exceed a line subtotal.", {
					position: toast.POSITION.TOP_CENTER,
				});
				await this.setState({loading: false});
				return;
			}
		}

		const bucketPoolSubmit = cloneOriginalBucketPool((selected[0] || {}).transactions?.tax_json);
		let totalsCash = 0;
		let totalsTax = 0;
		selected.forEach((row, i) => {
			const s = slices[i];
			const override = popMatchingOriginalBucketTax(
				bucketPoolSubmit,
				s.extension_pre_tax,
				s.rate,
				row.restocking_fee
			);
			const line = computeRefundLineCashAndTax(
				s.extension_pre_tax,
				s.rate,
				s.taxable,
				row.restocking_fee,
				override
			);
			totalsCash += line.cash;
			totalsTax += line.tax_refunded;
		});
		totalsCash = Math.round(totalsCash * 100) / 100;
		totalsTax = Math.round(totalsTax * 100) / 100;

		const cap =
			this.state.max_refundable_cash_remaining != null
				? Number(this.state.max_refundable_cash_remaining)
				: Number(this.state.original_transaction_total || 0);
		if (totalsCash > cap + 0.02) {
			API.ToastId = toast.error(
				"Refund exceeds what can still be returned on this sale ($" +
					cap.toFixed(2) +
					" remaining of $" +
					parseFloat(this.state.original_transaction_total || 0).toFixed(2) +
					" charged).",
				{position: toast.POSITION.TOP_CENTER}
			);
			await this.setState({loading: false});
			return;
		}

		this.state.restock = false;
		this.state.amount = totalsCash.toFixed(2);
		this.state.restocking_fee = "0.00";
		let sumQty = 0;
		this.state.items = selected.map((row) => {
			const fee = parseRestockFeeInput(row.restocking_fee).toFixed(2);
			const qtyForApi =
				row.transaction_type === "transaction_product"
					? 1
					: Math.max(1, parseInt(String(row.quantity_display ?? "").trim(), 10) || 0);
			sumQty += qtyForApi;
			return {
				...row,
				quantity: qtyForApi,
				restocking_fee: fee,
			};
		});
		this.state.quantity = sumQty;

		await this.setState({loading: false});
		await super.handleSubmit(event);
	};

	handleSelectionToggle = (pkey) => {
		const items = (this.state.items || []).map((row) =>
			row.pkey === pkey ? {...row, selected: !row.selected} : row
		);
		this.setState({items}, () => this.recalculatePreview());
	};

	handleQtyChange = (pkey, rawVal) => {
		const raw = rawVal === undefined || rawVal === null ? "" : String(rawVal);
		const items = (this.state.items || []).map((row) => {
			if (row.pkey !== pkey) return row;
			if (row.transaction_type === "transaction_product") {
				return {...row, quantity_display: "1", quantity_error: ""};
			}
			const trimmed = raw.trim();
			if (trimmed === "") {
				return {...row, quantity_display: "", quantity_error: ""};
			}
			const n = parseInt(trimmed, 10);
			if (isNaN(n)) {
				return {
					...row,
					quantity_display: raw,
					quantity_error: "Enter a valid whole number.",
				};
			}
			if (n < 1) {
				return {
					...row,
					quantity_display: raw,
					quantity_error: "Quantity must be at least 1.",
				};
			}
			if (n > row.original_quantity) {
				return {
					...row,
					quantity_display: raw,
					quantity_error:
						"Maximum quantity for this line is " + row.original_quantity + ".",
				};
			}
			return {
				...row,
				quantity_display: String(n),
				quantity_error: "",
				quantity: n,
			};
		});
		this.setState({items}, () => this.recalculatePreview());
	};

	handleRestockToggle = (pkey, val) => {
		const items = (this.state.items || []).map((row) =>
			row.pkey === pkey ? {...row, restock: val} : row
		);
		this.setState({items});
	};

	handleLineRestockingFeeChange = (pkey, value) => {
		const clean = sanitizeRestockFeeInput(value);
		const items = (this.state.items || []).map((row) =>
			row.pkey === pkey ? {...row, restocking_fee: clean} : row
		);
		this.setState({items}, () => this.recalculatePreview());
	};

	form = () => {
		const {properties} = this.model;
		const object = this.state;
		const selectedCount = (this.state.items || []).filter((i) => i.selected).length;
		const cashDiscPct = this.state.cash_discount_pct || 0;
		const taxJsonRawForm = (this.state.items || [])[0]?.transactions?.tax_json;
		const merchCtxForm = buildRefundMerchandiseScalingContextFromUi(
			this.state.items || [],
			taxJsonRawForm,
			cashDiscPct
		);
		const bucketPool = cloneOriginalBucketPool((this.state.items || [])[0]?.transactions?.tax_json);

		const itemList = (this.state.items || []).map((item) => {
			if (item.refunded === 1) {
				return null;
			}
			if (item.transaction_type === "transaction_trade") {
				return null;
			}

			const br = item.selected ? lineRefundBreakdown(item, cashDiscPct, bucketPool, merchCtxForm) : null;

			return (
				<div key={item.pkey} className={"row align-items-start mb-2"}>
					<div className={"col-4"}>
						<div className="form-group mb-0">
							<Framework.Elements.Checkbox
								value={!!item.selected}
								property={properties.refunded}
								error={object.refunded_error}
								update={() => this.handleSelectionToggle(item.pkey)}
								color='default'
								className='refund-checkbox'
								label={item.name}
							/>
						</div>
					</div>
					<div className={"col-2"}>
						<div className="form-group mb-0">
							{/*
							  Framework TextField syncs blur from this.state[property.name]; per-line keys are never
							  set there, so values disappeared. MUI field is fully controlled via items[] only.
							*/}
							<MuiLineQtyField
								size="small"
								fullWidth
								variant="outlined"
								label="Quantity"
								disabled={item.transaction_type === "transaction_product"}
								value={
									item.transaction_type === "transaction_product"
										? "1"
										: item.quantity_display ?? ""
								}
								error={!!item.quantity_error}
								helperText={item.quantity_error || ""}
								onChange={(e) => this.handleQtyChange(item.pkey, e.target.value)}
								inputProps={{inputMode: "numeric", "aria-label": "Refund line quantity"}}
							/>
						</div>
					</div>
					<div className={"col-3"}>
						{(item.transaction_type === "transaction_item" ||
							item.transaction_type === "transaction_consignment") && (
							<div className="form-group mb-0">
								<Framework.Elements.ButtonSwitch
									name={"restock"}
									property={{
										name: `refund_restock_${item.pkey}`,
										label: "Restock",
									}}
									value={!!item.restock}
									error={object.restock_error}
									update={(_property, value) => this.handleRestockToggle(item.pkey, value)}
									labelTrue={"Yes"}
									labelFalse={"No"}
									project={'pos'}
								/>
							</div>
						)}
						<div
							className="form-group mb-0"
							style={{
								marginTop:
									item.transaction_type === "transaction_item" ||
									item.transaction_type === "transaction_consignment"
										? 8
										: 0,
							}}
						>
							<MuiMoneyField
								size="small"
								fullWidth
								variant="outlined"
								label="Restock fee"
								value={item.restocking_fee ?? ""}
								error={!!item.restocking_fee_error}
								helperText={item.restocking_fee_error || ""}
								onChange={(e) => this.handleLineRestockingFeeChange(item.pkey, e.target.value)}
								inputProps={{inputMode: "decimal", "aria-label": "Line restocking fee"}}
							/>
						</div>
					</div>
					<div className={"col-3"}>
						<div className="small text-muted" style={{marginTop: 6}}>
							Line refund (est.)
						</div>
						{item.selected && br ? (
							<div style={{fontWeight: 600}}>
								<div className="small">
									Subtotal ${br.subtotal.toFixed(2)}
									{br.fee > 0 && <span> · Fee ${br.fee.toFixed(2)}</span>}
								</div>
								{br.taxReturned > 0 && (
									<div className="small text-muted" style={{marginTop: 2}}>
										Tax returned ${br.taxReturned.toFixed(2)}
									</div>
								)}
								<div className="small" style={{marginTop: 4}}>
									Cash back ${br.cash.toFixed(2)}
								</div>
							</div>
						) : (
							<div style={{fontWeight: 600}}>—</div>
						)}
					</div>
				</div>
			);
		});

		return (
			<fieldset>
				<Framework.Elements.LoadingSpinner isLoading={object.loading} />
				<h4 className="section-title">Refund items</h4>
				<p className="text-muted small">
					Select lines and quantities. Each line subtotal starts from quantity × cash-discounted unit price
					minus line discounts, then is scaled to match this sale&apos;s transaction-level discount and trade-ins
					(same rules as checkout). Restocking fees apply to that scaled subtotal; tax uses line
					rates (or a matching bucket when applicable). Cash back is (subtotal − fee) plus tax on
					the returned subtotal.
				</p>
				<div className={"row font-weight-bold small text-muted mb-1"}>
					<div className={"col-4"}>Item</div>
					<div className={"col-2"}>Quantity</div>
					<div className={"col-3"}>Restock</div>
					<div className={"col-3"}>Est. refund</div>
				</div>
				{itemList}
				<hr />
				<div className={"row"}>
					<div className={"col-md-6"}>
						<div className="p-3 bg-light rounded">
							<div className="d-flex justify-content-between">
								<span>Tax returned (est.)</span>
								<strong>${Number(this.state.previewTaxRefunded || 0).toFixed(2)}</strong>
							</div>
							<div className="d-flex justify-content-between mt-2">
								<span>Total cash back (est.)</span>
								<strong>${Number(this.state.refund_amount || 0).toFixed(2)}</strong>
							</div>
							<div className="small text-muted mt-2">
								{selectedCount} line{selectedCount === 1 ? "" : "s"} selected · Charged on sale $
								{parseFloat(this.state.original_transaction_total || 0).toFixed(2)}
								{this.state.cumulative_cash_refunded > 0.00001 && (
									<span>
										{" "}
										· Already refunded ${Number(this.state.cumulative_cash_refunded || 0).toFixed(2)}
									</span>
								)}
								{" "}
								· Max cash back this submission ~$
								{Number(this.state.max_refundable_cash_remaining ?? 0).toFixed(2)}
							</div>
						</div>
					</div>
				</div>

				<div className={"row mt-2"}>
					<div className={"col-md-6"}>
						<div className={"form-group"}>
							<Framework.Elements.Select
								shouldLoad={true}
								update={this.handleSelectPropertyUpdate}
								property={{...properties.method, class: false, name: "method"}}
								error={object.method_error}
								value={object.method}
								objects={[
									{pkey: "cash", name: "Cash"},
									...(this.state.has_processor_refund
										? [{pkey: "card", name: "Card"}]
										: []),
									...(this.state.has_zelle_tender
										? [{pkey: "zelle", name: "Zelle"}]
										: []),
									{pkey: "other", name: "Other"},
								]}
								text={"name"}
							/>
						</div>
						{String(this.state.method || "").toLowerCase() === "cash" && (
							<div className={"form-group"}>
								<Framework.Elements.Select
									shouldLoad={true}
									object={object.registers}
									value={object.register}
									error={object.register_error}
									model={Types.Registers._name}
									property={properties.register}
									label={"Register"}
									update={this.handleSelectPropertyUpdate}
								/>
							</div>
						)}
						<div className={"form-group"}>
							<Framework.Elements.TextField
								name={"note"}
								label={"Note"}
								multiline={"3"}
								update={this.handleTextFieldPropertyUpdate}
								error={object.note_error}
								property={properties.note}
								value={object.note}
							/>
						</div>
					</div>
				</div>
			</fieldset>
		);
	};
}
